import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { authService } from '../services/authService';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check active session
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserProfile(session?.user?.id);
      } else {
        setLoading(false);
      }
    })?.catch((err) => {
      console.error('Error getting session:', err);
      setError(err?.message || 'Failed to get session');
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserProfile(session?.user?.id);
      } else {
        setUserProfile(null);
        setLoading(false);
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  const loadUserProfile = async (userId) => {
    try {
      console.log('Loading user profile for userId:', userId);
      const profile = await authService?.getUserProfile(userId);
      console.log('User profile loaded:', profile);
      setUserProfile(profile);
      setError(null);
    } catch (error) {
      console.error('Error loading user profile:', error);
      console.error('Profile error details:', error?.message, error?.code);
      setUserProfile(null);
      // Don't set error here as it shouldn't block auth
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email, password, fullName, company) => {
    try {
      setError(null);
      const data = await authService?.signUp(email, password, fullName, company);
      return data;
    } catch (err) {
      setError(err?.message || 'Signup failed');
      throw err;
    }
  };

  const signIn = async (email, password) => {
    try {
      setError(null);
      const data = await authService?.signIn(email, password);
      return data;
    } catch (err) {
      setError(err?.message || 'Signin failed');
      throw err;
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      await authService?.signOut();
      setUser(null);
      setUserProfile(null);
    } catch (err) {
      setError(err?.message || 'Signout failed');
      throw err;
    }
  };

  const updateProfile = async (updates) => {
    try {
      if (!user) throw new Error('No user logged in');
      setError(null);
      const updatedProfile = await authService?.updateUserProfile(user?.id, updates);
      setUserProfile(updatedProfile);
      return updatedProfile;
    } catch (err) {
      setError(err?.message || 'Profile update failed');
      throw err;
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    error,
    signUp,
    signIn,
    signOut,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
